import { getCyclingAnglesForBikeType, getAngleStatus, BIKE_TYPES } from './CyclingAngles';
import { detectPedalCycles } from './PedalCycleDetector';
import { getRobustMeasurementAtPosition, calculateRobustStats } from './OutlierFilter';
import { debugPedalCycles, compareOldVsNew } from './DebugTools';

/**
 * CyclingAnalyzer: collects per-frame angle data, detects stable pedaling
 * periods from the data alone (no video inspection), and computes aggregate
 * results using only those stable frames.
 *
 * Stable cycling detection:
 *   - Knee angle must show rhythmic oscillation (std dev > threshold)
 *   - Torso angle must be in a plausible cycling range (not standing upright)
 *   - Landmarks must have reasonable visibility
 *
 * This automatically filters out: standing, getting on/off the bike,
 * sitting still, walking past the camera, etc.
 */

const WINDOW_SIZE = 30; // ~1 second at 30 fps
const KNEE_STD_THRESHOLD = 8; // degrees — oscillation needed to count as pedaling
const TORSO_MIN = 15; // if torso angle < 15° person is lying flat (unlikely cyclist)
const TORSO_MAX = 75; // if torso angle > 75° person is standing nearly upright
const MIN_STABLE_FRAMES = 30; // need at least 1 second of stable data

export class CyclingAnalyzer {
  constructor(bikeType = BIKE_TYPES.RECREATIONAL) {
    this.frames = []; // { timestamp, angles: { knee, hip, ankle, torso, elbow, shoulder } }
    this.stableRanges = []; // [{ start, end }] indices into this.frames
    this._results = null;
    this.totalFramesProcessed = 0; // total frames from video
    this.framesWithPose = 0; // frames where pose was detected
    this.bikeType = bikeType;
    this.pedalCycleData = null; // Detected pedal cycles and labeled frames
  }

  reset() {
    this.frames = [];
    this.stableRanges = [];
    this._results = null;
    this.totalFramesProcessed = 0;
    this.framesWithPose = 0;
    this.pedalCycleData = null;
  }

  setBikeType(bikeType) {
    this.bikeType = bikeType;
    this._results = null; // Invalidate cached results
  }

  /** Add a frame of computed angle data. */
  addFrame(angles, timestamp) {
    this.frames.push({ timestamp, angles: { ...angles } });
    this.framesWithPose++;
    this._results = null; // invalidate cached results
  }

  /** Increment total frames processed (called even when no pose detected). */
  incrementFrameCount() {
    this.totalFramesProcessed++;
  }

  get frameCount() {
    return this.frames.length;
  }

  /**
   * Detect stable cycling periods and return aggregate results.
   * Call this after video playback is complete (or periodically for live preview).
   */
  computeResults() {
    if (this._results) return this._results;
    
    const framesRejected = this.totalFramesProcessed - this.framesWithPose;
    
    if (this.frames.length < WINDOW_SIZE) {
      return {
        stable: false,
        reason: 'Not enough frames collected',
        stableFrameCount: 0,
        totalFramesProcessed: this.totalFramesProcessed,
        framesWithPose: this.framesWithPose,
        framesRejected,
        accuracy: 'insufficient',
      };
    }

    this._detectStableRanges();

    const stableFrames = this._getStableFrames();
    if (stableFrames.length < MIN_STABLE_FRAMES) {
      return {
        stable: false,
        reason: `Only ${stableFrames.length} stable frames detected (need ${MIN_STABLE_FRAMES}). Make sure the cyclist is actively pedaling.`,
        stableFrameCount: stableFrames.length,
        totalFrames: this.frames.length,
        totalFramesProcessed: this.totalFramesProcessed,
        framesWithPose: this.framesWithPose,
        framesRejected,
        accuracy: stableFrames.length >= 15 ? 'low' : 'insufficient',
      };
    }

    // Detect pedal cycles from stable frames
    this.pedalCycleData = detectPedalCycles(stableFrames);

    // Debug logging if enabled
    if (window.__CYCLING_DEBUG__) {
      console.log('🔍 DEBUG: Computing results...');
      debugPedalCycles(this.pedalCycleData, stableFrames);
    }

    const results = this._aggregateAngles(stableFrames);
    
    // Debug comparison if enabled
    if (window.__CYCLING_DEBUG__ && this.pedalCycleData && this.pedalCycleData.detected) {
      const comparison = compareOldVsNew(stableFrames, results);
      console.log('📊 Old vs New Comparison:', comparison);
    }
    
    this._results = results;
    return results;
  }

  /** Sliding window detection of stable pedaling periods. */
  _detectStableRanges() {
    this.stableRanges = [];
    const kneeValues = this.frames.map(f => f.angles.knee);
    const torsoValues = this.frames.map(f => f.angles.torso);
    let inStable = false;
    let rangeStart = 0;

    for (let i = WINDOW_SIZE; i <= this.frames.length; i++) {
      const windowKnee = kneeValues.slice(i - WINDOW_SIZE, i);
      const windowTorso = torsoValues.slice(i - WINDOW_SIZE, i);

      const kneeStd = stdDev(windowKnee);
      const torsoMean = mean(windowTorso);

      const isStable = (
        kneeStd > KNEE_STD_THRESHOLD &&
        torsoMean > TORSO_MIN &&
        torsoMean < TORSO_MAX &&
        !windowKnee.some(v => v === undefined || isNaN(v)) &&
        !windowTorso.some(v => v === undefined || isNaN(v))
      );

      if (isStable && !inStable) {
        rangeStart = i - WINDOW_SIZE;
        inStable = true;
      } else if (!isStable && inStable) {
        this.stableRanges.push({ start: rangeStart, end: i - 1 });
        inStable = false;
      }
    }

    if (inStable) {
      this.stableRanges.push({ start: rangeStart, end: this.frames.length - 1 });
    }
  }

  _getStableFrames() {
    const frames = [];
    for (const range of this.stableRanges) {
      for (let i = range.start; i <= range.end; i++) {
        frames.push(this.frames[i]);
      }
    }
    return frames;
  }

  /**
   * Aggregate angles across stable frames using pedal position-based measurements.
   * Dynamic angles (knee, hip, ankle) → measured at specific pedal positions (BDC/TDC).
   * Static angles (torso, elbow, shoulder) → median across all stable frames.
   */
  _aggregateAngles(stableFrames) {
    const angleResults = {};
    const cyclingAngles = getCyclingAnglesForBikeType(this.bikeType);

    for (const angleDef of cyclingAngles) {
      const values = stableFrames
        .map(f => f.angles[angleDef.id])
        .filter(v => v !== undefined && !isNaN(v));

      if (values.length === 0) {
        angleResults[angleDef.id] = { valid: false };
        continue;
      }

      let evaluatedValue;
      let measurementInfo = {};

      // Use pedal position-based measurement for dynamic angles
      if (this.pedalCycleData && this.pedalCycleData.labeledFrames.length > 0) {
        const { labeledFrames } = this.pedalCycleData;

        if (angleDef.id === 'knee' && angleDef.evaluateAt === 'min') {
          // Knee: measure at BDC (minimum flexion = maximum extension)
          const bdcMeasurement = getRobustMeasurementAtPosition(labeledFrames, 'BDC', 'knee');
          if (bdcMeasurement) {
            evaluatedValue = bdcMeasurement.value;
            measurementInfo = {
              measurementPosition: 'BDC (Bottom Dead Center)',
              samplesUsed: bdcMeasurement.count,
              outliersRemoved: bdcMeasurement.outliersRemoved,
              method: 'pedal-position-based',
            };
          }
        } else if (angleDef.id === 'hip' && angleDef.evaluateAt === 'min') {
          // Hip: measure at TDC (most closed position)
          const tdcMeasurement = getRobustMeasurementAtPosition(labeledFrames, 'TDC', 'hip');
          if (tdcMeasurement) {
            evaluatedValue = tdcMeasurement.value;
            measurementInfo = {
              measurementPosition: 'TDC (Top Dead Center)',
              samplesUsed: tdcMeasurement.count,
              outliersRemoved: tdcMeasurement.outliersRemoved,
              method: 'pedal-position-based',
            };
          }
        } else if (angleDef.id === 'ankle' && angleDef.evaluateAt === 'range') {
          // Ankle: measure range across each cycle
          const ankleValues = labeledFrames
            .map(f => f.angles.ankle)
            .filter(v => v !== undefined && !isNaN(v));
          
          if (ankleValues.length > 0) {
            const stats = calculateRobustStats(ankleValues);
            if (stats) {
              evaluatedValue = stats.median;
              measurementInfo = {
                measurementPosition: 'Full pedal cycle',
                samplesUsed: stats.count,
                outliersRemoved: stats.outliersRemoved,
                method: 'pedal-position-based',
                range: stats.range,
              };
            }
          }
        }
      }

      // Fallback to traditional method if pedal detection failed or for static angles
      if (evaluatedValue === undefined) {
        const stats = calculateRobustStats(values);
        
        if (!stats || stats.median === null) {
          angleResults[angleDef.id] = { valid: false };
          continue;
        }

        switch (angleDef.evaluateAt) {
          case 'min':
            evaluatedValue = stats.min;
            measurementInfo = {
              measurementPosition: 'Minimum value (pedal detection unavailable)',
              samplesUsed: stats.count,
              outliersRemoved: stats.outliersRemoved,
              method: 'statistical-fallback',
            };
            break;
          case 'max':
            evaluatedValue = stats.max;
            measurementInfo = {
              measurementPosition: 'Maximum value',
              samplesUsed: stats.count,
              outliersRemoved: stats.outliersRemoved,
              method: 'statistical-fallback',
            };
            break;
          case 'mean':
          case 'range':
            evaluatedValue = stats.median;
            measurementInfo = {
              measurementPosition: 'Median across all frames',
              samplesUsed: stats.count,
              outliersRemoved: stats.outliersRemoved,
              method: 'median',
            };
            break;
          default:
            evaluatedValue = stats.median;
            measurementInfo = {
              measurementPosition: 'Median',
              samplesUsed: stats.count,
              outliersRemoved: stats.outliersRemoved,
              method: 'median',
            };
        }
      }

      const status = getAngleStatus(angleDef, evaluatedValue);

      // Calculate full stats for display
      const fullStats = calculateRobustStats(values);
      let rangeWarning = null;
      if (angleDef.evaluateAt === 'range' && angleDef.maxRange) {
        const range = measurementInfo.range || (fullStats ? fullStats.range : 0);
        if (range > angleDef.maxRange) {
          rangeWarning = {
            range: Math.round(range * 10) / 10,
            maxRange: angleDef.maxRange,
            message: angleDef.riskRange,
          };
        }
      }

      angleResults[angleDef.id] = {
        valid: true,
        min: fullStats ? fullStats.min : evaluatedValue,
        max: fullStats ? fullStats.max : evaluatedValue,
        mean: fullStats ? fullStats.mean : evaluatedValue,
        range: fullStats ? fullStats.range : 0,
        evaluatedValue: Math.round(evaluatedValue * 10) / 10,
        status,
        rangeWarning,
        sampleCount: values.length,
        ...measurementInfo,
      };
    }

    // Estimate cadence from knee angle oscillation
    const kneeValues = stableFrames.map(f => f.angles.knee);
    const timestamps = stableFrames.map(f => f.timestamp);
    const cadence = estimateCadence(kneeValues, timestamps);

    // Determine accuracy based on stable frame count and pedal detection
    let accuracy;
    const hasPedalDetection = this.pedalCycleData && this.pedalCycleData.totalCycles >= 3;
    
    if (stableFrames.length >= 90 && hasPedalDetection) {
      accuracy = 'high'; // 3+ seconds of stable data with good pedal detection
    } else if (stableFrames.length >= 60 && hasPedalDetection) {
      accuracy = 'good'; // 2+ seconds with pedal detection
    } else if (stableFrames.length >= MIN_STABLE_FRAMES) {
      accuracy = 'moderate'; // 1+ seconds (minimum)
    } else {
      accuracy = 'low';
    }

    const framesRejected = this.totalFramesProcessed - this.framesWithPose;

    return {
      stable: true,
      stableFrameCount: stableFrames.length,
      totalFrames: this.frames.length,
      totalFramesProcessed: this.totalFramesProcessed,
      framesWithPose: this.framesWithPose,
      framesRejected,
      stablePercentage: Math.round((stableFrames.length / this.frames.length) * 100),
      angles: angleResults,
      cadenceRPM: cadence,
      accuracy,
      bikeType: this.bikeType,
      pedalCycles: this.pedalCycleData ? {
        totalCycles: this.pedalCycleData.totalCycles,
        validBDCFrames: this.pedalCycleData.validBDCFrames,
        validTDCFrames: this.pedalCycleData.validTDCFrames,
        detected: true,
      } : {
        detected: false,
        reason: 'Pedal position detection failed or insufficient data',
      },
    };
  }
}

// ── Helpers ──────────────────────────────────────────────────────

function mean(arr) {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stdDev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  const variance = arr.reduce((sum, v) => sum + (v - m) ** 2, 0) / arr.length;
  return Math.sqrt(variance);
}

/**
 * Estimate cadence (RPM) by counting zero-crossings of the
 * de-meaned knee angle signal.
 */
function estimateCadence(kneeValues, timestamps) {
  if (kneeValues.length < 10 || timestamps.length < 10) return null;

  const m = mean(kneeValues);
  const centered = kneeValues.map(v => v - m);

  let crossings = 0;
  for (let i = 1; i < centered.length; i++) {
    if ((centered[i - 1] < 0 && centered[i] >= 0) ||
        (centered[i - 1] >= 0 && centered[i] < 0)) {
      crossings++;
    }
  }

  // Each full pedal revolution = 2 zero crossings (up-cross + down-cross)
  const revolutions = crossings / 2;
  const durationMs = timestamps[timestamps.length - 1] - timestamps[0];
  if (durationMs <= 0) return null;

  const durationMin = durationMs / 60000;
  return Math.round(revolutions / durationMin);
}
